import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon, { type IconName } from '../components/Brand/Icon';
import { FactList, RouteDiagram, type Way } from '../components/Send/Ways';
import { usePageTitle } from '../hooks/usePageTitle';
import { choiceSearch, WHATS, type What } from '../lib/sendWizard';
import ui from '../styles/ui.module.css';
import styles from './LandingPage.module.css';

/**
 * / — what we stand for, and a way in (docs/adr/0014). Visitors are steered
 * into the send wizard: its first step is right here in the hero. People who
 * know what they want have direct links further down.
 */

const WHAT_ICONS: Record<What, IconName> = { text: 'text', small: 'file', large: 'box' };

const FACTS: Record<Way, [key: 'when' | 'stored' | 'size', icon: IconName][]> = {
  drop: [
    ['when', 'clock'],
    ['stored', 'flame'],
    ['size', 'text'],
  ],
  live: [
    ['when', 'people'],
    ['stored', 'notStored'],
    ['size', 'box'],
  ],
  relay: [
    ['when', 'people'],
    ['stored', 'notStored'],
    ['size', 'size'],
  ],
};

const PROMISES: [key: string, icon: IconName][] = [
  ['nothingKept', 'timer'],
  ['code', 'code'],
  ['noAccount', 'noAccount'],
  ['noTrackers', 'shield'],
];

const DIRECT = [
  ['burn', '/new'],
  ['live', '/live'],
  ['files', '/live?kind=files'],
  ['lock', '/tools'],
] as const;

export default function LandingPage() {
  const { t } = useTranslation();
  usePageTitle();

  return (
    <main>
      <section className={styles.hero} aria-labelledby="landing-title">
        <div className={styles.heroText}>
          <p className={styles.pill}>{t('landing.badge')}</p>
          <h1 id="landing-title" className={styles.title}>
            {t('landing.titleLine1')}
            <br />
            <span className={styles.accent}>{t('landing.titleLine2')}</span>
          </h1>
          <p className={styles.lead}>{t('landing.lead')}</p>
          <div className={styles.actions}>
            <Link to="/send" className={ui.button}>
              {t('landing.start')}
              <Icon name="arrow" size={18} strokeWidth={2} />
            </Link>
            <a href="#ways" className={styles.strongLink} onClick={scrollToWays}>
              {t('landing.compare')}
            </a>
          </div>
          <p className={styles.small}>{t('landing.steps')}</p>
        </div>

        <section className={`${ui.panel} ${styles.pick}`} aria-labelledby="pick-title">
          <p className={styles.eyebrow}>{t('landing.pick.step')}</p>
          <h2 id="pick-title" className={styles.pickTitle}>
            {t('landing.pick.title')}
          </h2>
          <ul className={styles.tiles}>
            {WHATS.map((what) => (
              <li key={what}>
                <Link to={`/send${choiceSearch(what)}`} className={styles.tile}>
                  <span className={styles.tileIcon}>
                    <Icon name={WHAT_ICONS[what]} size={22} />
                  </span>
                  <span className={styles.tileText}>
                    <span className={styles.tileTitle}>{t(`send.what.${what}.title`)}</span>
                    <span className={styles.tileBody}>
                      {t(`send.what.${what}.body`)} · {t(`send.what.${what}.limit`)}
                    </span>
                  </span>
                  <Icon name="arrow" size={18} strokeWidth={2} className={styles.tileArrow} />
                </Link>
              </li>
            ))}
          </ul>
          <p className={styles.small}>{t('landing.pick.next')}</p>
        </section>
      </section>

      <section className={styles.band} aria-labelledby="zk-title">
        <div className={styles.inner}>
          <p className={styles.eyebrow}>{t('landing.zk.eyebrow')}</p>
          <h2 id="zk-title" className={styles.sectionTitle}>
            {t('landing.zk.title')}
          </h2>
          <p className={styles.sectionLead}>{t('landing.zk.lead')}</p>

          <ol className={styles.journey} aria-label={t('landing.zk.stepsLabel')}>
            <li className={styles.stop}>
              <h3 className={styles.stopTitle}>
                <span className={styles.stopNumber}>1</span>
                {t('landing.zk.you.title')}
              </h3>
              <div className={styles.stopShow} aria-hidden="true">
                <span className={styles.plain}>{t('landing.zk.sample')}</span>
                <Icon name="arrow" size={18} strokeWidth={2} />
                <span className={styles.lockChip}>
                  <Icon name="lock" size={18} strokeWidth={2} />
                </span>
              </div>
              <p className={styles.stopBody}>{t('landing.zk.you.body')}</p>
            </li>
            <li className={`${styles.stop} ${styles.stopServer}`}>
              <h3 className={styles.stopTitle}>
                <span className={`${styles.stopNumber} ${styles.stopNumberServer}`}>2</span>
                {t('landing.zk.server.title')}
              </h3>
              <div className={styles.stopShow} aria-hidden="true">
                <span className={styles.scrambled}>{t('landing.zk.scrambled')}</span>
              </div>
              <p className={styles.stopBody}>{t('landing.zk.server.body')}</p>
            </li>
            <li className={styles.stop}>
              <h3 className={styles.stopTitle}>
                <span className={styles.stopNumber}>3</span>
                {t('landing.zk.them.title')}
              </h3>
              <div className={styles.stopShow} aria-hidden="true">
                <span className={styles.lockChip}>
                  <Icon name="unlock" size={18} strokeWidth={2} />
                </span>
                <Icon name="arrow" size={18} strokeWidth={2} />
                <span className={styles.plain}>{t('landing.zk.sample')}</span>
              </div>
              <p className={styles.stopBody}>{t('landing.zk.them.body')}</p>
            </li>
          </ol>

          <div className={styles.keyNote}>
            <span className={styles.keyLink} aria-hidden="true">
              {t('landing.zk.keyLink')}
              <strong>{t('landing.zk.keyFragment')}</strong>
            </span>
            <p>
              <strong>{t('landing.zk.keyStrong')}</strong> {t('landing.zk.keyBody')}
            </p>
          </div>

          <ul className={styles.promises}>
            {PROMISES.map(([key, icon]) => (
              <li key={key} className={styles.promise}>
                <span className={styles.promiseIcon}>
                  <Icon name={icon} />
                </span>
                <div>
                  <h3 className={styles.promiseTitle}>{t(`landing.promises.${key}.title`)}</h3>
                  <p className={styles.promiseBody}>
                    {t(`landing.promises.${key}.body`)}
                    {key === 'code' && (
                      <>
                        {' '}
                        <Link to="/how-it-works#code">{t('landing.promises.code.link')}</Link>.
                      </>
                    )}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section id="ways" className={styles.inner} aria-labelledby="ways-title" tabIndex={-1}>
        <p className={styles.eyebrow}>{t('landing.ways.eyebrow')}</p>
        <h2 id="ways-title" className={styles.sectionTitle}>
          {t('landing.ways.title')}
        </h2>
        <p className={styles.sectionLead}>{t('landing.ways.lead')}</p>

        <div className={styles.ways}>
          {(['drop', 'live', 'relay'] as const).map((way) => (
            <article key={way} className={`${styles.way} ${way === 'live' ? styles.wayFeatured : ''}`} aria-labelledby={`way-${way}`}>
              <div className={styles.wayTop}>
                <p className={styles.eyebrow}>{t(`landing.ways.${way}.eyebrow`)}</p>
                {way !== 'drop' && <span className={way === 'live' ? styles.badgeStrong : styles.badge}>{t(`landing.ways.${way}.badge`)}</span>}
              </div>
              <h3 id={`way-${way}`} className={styles.wayTitle}>
                {t(`landing.ways.${way}.title`)}
              </h3>
              <p className={styles.wayLead}>{t(`landing.ways.${way}.lead`)}</p>
              <RouteDiagram way={way} />
              <FactList
                facts={FACTS[way].map(([fact, icon]) => ({
                  icon,
                  strong: t(`landing.ways.${way}.facts.${fact}.strong`),
                  rest: t(`landing.ways.${way}.facts.${fact}.rest`),
                }))}
              />
              <p className={styles.best}>{t(`landing.ways.${way}.best`)}</p>
            </article>
          ))}
        </div>
        <p className={styles.wizardLink}>
          <Link to="/send" className={styles.strongLink}>
            {t('landing.ways.wizard')} <span aria-hidden="true">→</span>
          </Link>
        </p>
      </section>

      <section className={styles.direct} aria-labelledby="direct-title">
        <div className={styles.inner}>
          <h2 id="direct-title" className={styles.directTitle}>
            {t('landing.direct.title')}
          </h2>
          <p className={styles.small}>{t('landing.direct.lead')}</p>
          <ul className={styles.directList}>
            {DIRECT.map(([key, to]) => (
              <li key={key}>
                <Link to={to} className={styles.directLink}>
                  <span className={styles.directName}>{t(`landing.direct.${key}.title`)}</span>
                  <span className={styles.tileBody}>{t(`landing.direct.${key}.body`)}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </main>
  );
}

// A plain #ways link would work, but move focus too so keyboard users land there.
function scrollToWays(e: React.MouseEvent) {
  const target = document.getElementById('ways');
  if (!target) return;
  e.preventDefault();
  target.scrollIntoView({ behavior: 'smooth' });
  target.focus({ preventScroll: true });
}
